import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { VolatilityViewEntry } from '@/services/mappers/volatilityMapper'
import { formatLocalDate, formatVND } from '@/utils/format'
import { IconArrowDownRight, IconArrowUpRight, IconMinus } from '@tabler/icons-react'

interface PriceVolatilityListProps {
  entries: VolatilityViewEntry[]
  isLoading?: boolean
  isError?: boolean
}

export function PriceVolatilityList({ entries, isLoading, isError }: PriceVolatilityListProps) {
  if (isLoading) {
    // Mobile skeleton
    const mobileSkeleton = (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <Card key={i} className="p-4">
            <div className="space-y-3">
              <div className="flex justify-between">
                <Skeleton className="h-5 w-24" />
                <Skeleton className="h-5 w-20" />
              </div>
              <div className="space-y-2">
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-full" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    )

    // Desktop skeleton
    const desktopSkeleton = (
      <Card>
        <CardContent className="p-6 space-y-3">
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} className="flex gap-3 items-center">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-24" />
            </div>
          ))}
        </CardContent>
      </Card>
    )

    return (
      <>
        <div className="block md:hidden">{mobileSkeleton}</div>
        <div className="hidden md:block">{desktopSkeleton}</div>
      </>
    )
  }

  if (isError) {
    return (
      <Card className="w-full max-w-full overflow-hidden">
        <CardContent className="p-6 text-sm text-destructive">
          Không thể tải báo cáo biến động. Vui lòng thử lại.
        </CardContent>
      </Card>
    )
  }

  if (!entries.length) {
    return (
      <Card className="w-full max-w-full overflow-hidden">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Không có dữ liệu trong khoảng thời gian đã chọn.
        </CardContent>
      </Card>
    )
  }

  // Mobile card view
  const mobileView = (
    <div className="space-y-3">
      {entries
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
        .map(entry => (
          <Card key={entry.date} className="p-4 w-full max-w-full overflow-hidden">
            {/* Header: Date + Direction */}
            <div className="flex justify-between items-start gap-2 mb-3">
              <time className="text-base font-semibold text-foreground shrink-0">
                {formatLocalDate(entry.date)}
              </time>
              <div className="flex items-center gap-2 shrink-0">
                <Direction direction={entry.direction} />
                {entry.swingFlag && (
                  <Badge variant="secondary" className="text-xs">
                    Biến động mạnh
                  </Badge>
                )}
              </div>
            </div>

            {/* Content */}
            <div className="space-y-2 w-full max-w-full overflow-hidden">
              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">Giá vàng</span>
                <span className="text-sm font-medium text-foreground shrink-0">
                  {formatVND(entry.goldPrice)}
                </span>
              </div>

              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">Thay đổi giá</span>
                <span className="text-sm font-medium text-foreground shrink-0">
                  {formatVND(entry.priceDelta)}
                </span>
              </div>

              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">Giá trị tài sản</span>
                <span className="text-sm font-medium text-foreground shrink-0">
                  {formatVND(entry.assetValue)}
                </span>
              </div>

              <div className="flex justify-between items-center gap-2">
                <span className="text-sm text-muted-foreground shrink-0">Thay đổi giá trị</span>
                <span className="text-sm font-medium text-foreground shrink-0">
                  {formatVND(entry.assetValueDelta)}
                </span>
              </div>

              {entry.missingPrice && (
                <div className="pt-2">
                  <Badge variant="secondary" className="text-xs">
                    Thiếu giá gốc
                  </Badge>
                </div>
              )}
            </div>
          </Card>
        ))}
    </div>
  )

  // Desktop table view
  const desktopView = (
    <Card className="w-full max-w-full overflow-hidden">
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ngày</TableHead>
                <TableHead>Giá vàng</TableHead>
                <TableHead>Thay đổi giá</TableHead>
                <TableHead>Giá trị tài sản</TableHead>
                <TableHead>Thay đổi giá trị</TableHead>
                <TableHead>Ghi chú</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries
                .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                .map(entry => (
                  <TableRow key={entry.date}>
                    <TableCell className="whitespace-nowrap">
                      {formatLocalDate(entry.date)}
                    </TableCell>
                    <TableCell className="whitespace-nowrap font-medium">
                      {formatVND(entry.goldPrice)}
                    </TableCell>
                    <TableCell className="flex items-center gap-2">
                      <Direction direction={entry.direction} />
                      <span>{formatVND(entry.priceDelta)}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatVND(entry.assetValue)}
                    </TableCell>
                    <TableCell className="flex items-center gap-2">
                      <Direction direction={entry.direction} />
                      <span>{formatVND(entry.assetValueDelta)}</span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                      {entry.missingPrice ? (
                        <Badge variant="secondary">Thiếu giá</Badge>
                      ) : entry.notes ? (
                        entry.notes
                      ) : (
                        '-'
                      )}
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <>
      <div className="block md:hidden">{mobileView}</div>
      <div className="hidden md:block">{desktopView}</div>
    </>
  )
}

function Direction({ direction }: { direction: VolatilityViewEntry['direction'] }) {
  if (direction === 'up') {
    return <IconArrowUpRight className="h-4 w-4 text-emerald-500" stroke={1.75} />
  }
  if (direction === 'down') {
    return <IconArrowDownRight className="h-4 w-4 text-red-500" stroke={1.75} />
  }
  return <IconMinus className="h-4 w-4 text-muted-foreground" stroke={1.75} />
}
